import { useState, type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SiteGuardProvider } from 'src/contexts/SiteGuardContext';
import { createStore, createPersistor } from './redux/store';
import { initSentry } from './sentryBrowser';
import { registerChunkReloadHandler } from './chunkReload';

// Tailwind base + global CSS. Used to be imported by src/main.jsx, but
// that entry was deleted in Phase 1 when Vike took over rendering — so
// without this import nothing applies the Tailwind reset and pages
// render unstyled. Importing here covers all routes since Root wraps
// every Vike page.
import './index.css';

// Sentry boots once at module load. Root is imported only by client-only
// page modules (see pages/index/+Page.tsx with ssr: false), and
// initSentry() is a no-op on the server anyway (typeof window guard).
initSentry();

// Recover tabs left open across a deploy: a stale dynamic-import (chunk)
// load failure triggers a one-time reload to pull the fresh bundle graph
// (NPT-1358). No-op on the server (typeof window guard inside).
registerChunkReloadHandler();

// Exported so SSR-delivered data can be seeded into the cache before the tree
// mounts (see src/ssr/seedLandingQueries.ts). Module-level on purpose: one
// cache per document, shared by every observer of a given key.
// eslint-disable-next-line react-refresh/only-export-components -- the cache must stay in the module that mounts the provider; relocating it would move app bootstrap state for a cosmetic HMR boundary
export const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         refetchOnWindowFocus: false,
      },
   },
});

// Outer provider stack. Each mount gets its own Redux store + persistor
// via useState's lazy initializer — so the store isn't a module-level
// singleton (would leak request state on the server when a route ever
// flips to body SSR) and isn't recreated on every render.
//
// On the server createPersistor() returns null (gated in
// src/redux/store.ts), so we skip <PersistGate> there — react-redux's
// <Provider> is happy with the non-rehydrated store. redux-persist's
// hydration only matters on the client and runs after onRenderClient
// mounts this tree.
export default function Root({ children }: { children: ReactNode }) {
   const [store] = useState(() => createStore());
   const [persistor] = useState(() => createPersistor(store));
   return (
      <Provider store={store}>
         {persistor ? (
            <PersistGate loading={null} persistor={persistor}>
               <QueryClientProvider client={queryClient}>
                  <SiteGuardProvider>{children}</SiteGuardProvider>
               </QueryClientProvider>
            </PersistGate>
         ) : (
            <QueryClientProvider client={queryClient}>
               <SiteGuardProvider>{children}</SiteGuardProvider>
            </QueryClientProvider>
         )}
      </Provider>
   );
}
